import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import FormikInput from '@acko-tech/building-blocks.ui.form';
import InputField from 'components/auto-lob/dealerForm/form/inputField';
import {
    Container,
    Heading,
    Wrapper,
    ButtonContainer,
    ModalHeading,
    Trailer,
} from './styles';
import RadioField from 'components/auto-lob/dealerForm/form/radioField';
import YearMonthSelector from 'components/auto-lob/dealerForm/form/yearMonthSelector';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import Calendar from '@acko-tech/building-blocks.ui.calendar';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import Header from 'widgets/header';
import { isEmpty } from 'utils/generics';

import validate from './useFormData/utils/validate';
import { useFormData } from './useFormData/index';
import { formatDate, getRegistrationYears } from './useFormData/utils/helper';
import {
    getTotalPremiumInclGst,
    hasMeaningfulPlanPrice,
} from './useFormData/utils/quotePriceResponse';
import { ACTIONS } from './useFormData/actions';

import { useMmvData } from './useMmvData';
import MmvOptions from 'components/auto-lob/dealerForm/form/mmvOptions';
import AddonsOptions, { SelectedAddonsSummary } from './form/addonsOptions';
import {
    PremiumBreakdownBox,
    PremiumBreakdownRow,
    PremiumBreakdownTotal,
} from './form/addonsOptions/styles';
import {
    formatAddonDisplayName,
    formatAddonRupee,
} from './form/addonsOptions/format';
import { allFuelTypes } from './useMmvData/utils/mapping';
import {
    getHeadingText,
    getFuelType,
    getVariantType,
    handleNextModal,
} from './useMmvData/utils/helper';
import { TMmv } from './useMmvData/types';
import { ACTIONS as MMV_ACTIONS } from './useMmvData/actions';

import { BasicLoader } from '@acko-tech/building-blocks.ui.loaders';
import { months } from './useFormData/utils/constants';
import TrailerInputGroup from './form/trailerInputGroup';

const Form = () => {
    const { isMobile } = useResponsiveDevice();
    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    const [
        showRegistrationyarMonthCalendar,
        setShowRegistrationYearMonthCalendar,
    ] = useState(false);

    const [mmvType, setMmvType] = useState<TMmv>('make');
    const [showMmvModal, setShowMmvModal] = useState(false);
    const [showTrailersModal, setShowTrailersModal] = useState(false);
    const [showFormSuccessModal, setShowFormSuccessModal] = useState(false);
    const [showAddonsModal, setShowAddonsModal] = useState(false);

    const { state, dispatch, setShouldScrollIntoView, submitFormData } =
        useFormData();
    const { mmvStore, dispatch: mmvDispatch } = useMmvData();

    const { isSuccess: isMmvApiSuccess, isFetching: isMmvApiFetching } =
        mmvStore;
    const { vehicles, popular_vehicles: popularVehicles } = mmvStore.mmvData;
    const setShowError = (val) => {
        dispatch({
            type: ACTIONS.SET_SHOW_ERROR,
            payload: val,
        });
    };

    const setFormData = ({ key, value }) => {
        dispatch({
            type: ACTIONS.SET_FORM_DATA,
            payload: {
                key,
                value,
            },
        });
    };

    const setError = ({ key, value }) => {
        dispatch({
            type: ACTIONS.SET_ERRORS,
            payload: {
                key,
                value,
            },
        });
    };

    const deleteError = ({ key }) => {
        dispatch({
            type: ACTIONS.DELETE_ERROR,
            payload: {
                key,
            },
        });
    };

    const resetMmv = () => {
        dispatch({
            type: ACTIONS.RESET_MMV,
        });
    };

    const setTrailerInfo = useCallback(({ key, value, id }) => {
        dispatch({
            type: ACTIONS.SET_TRAILERS_INFO,
            payload: {
                key,
                value,
                id,
            },
        });
    }, []);

    const setTrailerInfoError = useCallback(({ key, value, id }) => {
        dispatch({
            type: ACTIONS.SET_TRAILER_INFO_ERROR,
            payload: {
                key,
                value,
                id,
            },
        });
    }, []);

    const deleteTrailerInfoError = useCallback(({ key, id }) => {
        dispatch({
            type: ACTIONS.DELETE_TRAILER_INFO_ERROR,
            payload: {
                key,
                id,
            },
        });
    }, []);

    const {
        errors,
        trailerInfoErrors,
        showError,
        name,
        phoneNumber,
        email,
        pincode,
        address,
        vehicleType,
        registrationNumber,
        registrationYear,
        registrationMonth,
        engineNumber,
        chassisNumber,
        isVehicleFinanced,
        financierName,
        isPreviousPolicyExpired,
        claimedInPreviousPolicy,
        previousPolicyInsurer,
        totalAmount,
        quotePlan,
        addonsAvailable,
        make,
        model,
        policyStartDate,
        numberOfTrailers,
        trailersInfo,
        previousPolicyExpiryDate,
        formSubmitted,
        selectedAddons,
    } = state;

    const selectedAddonIds = useMemo(
        () => new Set((selectedAddons ?? []).map((a) => a.id)),
        [selectedAddons]
    );

    const apiPlanPrice = quotePlan?.price;
    const hasApiPricing = hasMeaningfulPlanPrice(apiPlanPrice);

    const addonsCatalog = addonsAvailable ?? [];

    const subtotalExclGst =
        hasApiPricing && apiPlanPrice?.netPremium != null
            ? apiPlanPrice.netPremium
            : null;

    const gstAmount =
        hasApiPricing && apiPlanPrice?.gst != null ? apiPlanPrice.gst : null;

    const grandTotalInclGst = getTotalPremiumInclGst(quotePlan);

    const showPremiumBreakdown =
        hasApiPricing ||
        totalAmount != null ||
        (selectedAddons?.length ?? 0) > 0;

    useEffect(() => {
        mmvDispatch({
            type: MMV_ACTIONS.GET_MMV_DATA,
            payload: {
                vehicle_type: vehicleType?.value,
            },
        });
    }, [vehicleType?.value]);

    const prevVehicleTypeRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!vehicleType?.value) return;
        const v = vehicleType.value;
        if (
            prevVehicleTypeRef.current !== undefined &&
            prevVehicleTypeRef.current !== v
        ) {
            setFormData({ key: 'selectedAddons', value: [] });
            setFormData({ key: 'addonsAvailable', value: [] });
            setFormData({ key: 'quotePlan', value: null });
        }
        prevVehicleTypeRef.current = v;
    }, [vehicleType?.value]);

    useEffect(() => {
        if (formSubmitted) {
            setShowFormSuccessModal(true);
        }
    }, [formSubmitted]);

    const fuelType = getFuelType({ allFuelTypes, make });

    const variantTypes = getVariantType({
        allFuelTypes,
        make,
        model,
    });

    const years = getRegistrationYears();

    return isMmvApiFetching && !isMmvApiSuccess ? (
        <BasicLoader />
    ) : (
        <>
            <Wrapper>
                <Container>
                    <Header />
                    <Heading>Please enter following details</Heading>

                    <FormikInput
                        name="name"
                        initialValue={name}
                        validate={(values) => {
                            const nameErrors = validate.name(values);
                            if (nameErrors.name) {
                                setError({
                                    key: 'name',
                                    value: nameErrors.name,
                                });
                            } else {
                                deleteError({ key: 'name' });
                            }
                            return nameErrors;
                        }}
                        value={name}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Full Name"
                                name="name"
                                onChange={(value, name) => {
                                    setFormData({ key: name, value });
                                }}
                                value={name}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <FormikInput
                        name="phoneNumber"
                        initialValue={phoneNumber}
                        validate={(values) => {
                            const phoneNumberErrors =
                                validate.phoneNumber(values);

                            if (phoneNumberErrors.phoneNumber) {
                                setError({
                                    key: 'phoneNumber',
                                    value: phoneNumberErrors.phoneNumber,
                                });
                            } else {
                                deleteError({
                                    key: 'phoneNumber',
                                });
                            }
                            return phoneNumberErrors;
                        }}
                        value={phoneNumber}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Phone number"
                                name="phoneNumber"
                                onChange={(value, name) => {
                                    setFormData({ key: name, value });
                                }}
                                value={phoneNumber}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <FormikInput
                        name="email"
                        initialValue={email}
                        validate={(values) => {
                            const emailErrors = validate.email(values);
                            if (emailErrors.email) {
                                setError({
                                    key: 'email',
                                    value: emailErrors.email,
                                });
                            } else {
                                deleteError({
                                    key: 'email',
                                });
                            }
                            return emailErrors;
                        }}
                        value={email}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Email"
                                name="email"
                                onChange={(value, name) => {
                                    setFormData({ key: name, value });
                                }}
                                value={email}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <FormikInput
                        name="pincode"
                        initialValue={pincode}
                        validate={(values) => {
                            const pincodeErrors = validate.pincode(values);
                            if (pincodeErrors.pincode) {
                                setError({
                                    key: 'pincode',
                                    value: pincodeErrors.pincode,
                                });
                            } else {
                                deleteError({
                                    key: 'pincode',
                                });
                            }
                            return pincodeErrors;
                        }}
                        value={pincode}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Pincode"
                                name="pincode"
                                onChange={(value, name) => {
                                    setFormData({ key: name, value });
                                }}
                                value={pincode}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <FormikInput
                        name="address"
                        initialValue={address}
                        validate={(values) => {
                            const addressErrors = validate.address(values);
                            if (addressErrors.address) {
                                setError({
                                    key: 'address',
                                    value: addressErrors.address,
                                });
                            } else {
                                deleteError({
                                    key: 'address',
                                });
                            }
                            return addressErrors;
                        }}
                        value={address}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Address"
                                name="address"
                                onChange={(value, name) => {
                                    setFormData({ key: name, value });
                                }}
                                value={address}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <RadioField
                        field="Vehicle type?"
                        selectedValue={vehicleType}
                        name={'vehicleType'}
                        setValue={(selection, key) => {
                            resetMmv();
                            setFormData({ key, value: selection });
                            if (selection.value === 'tractor') {
                                setError({
                                    key: 'numberOfTrailers',
                                    value: 'Number of trailers is required',
                                });
                            } else {
                                setFormData({
                                    key: 'numberOfTrailers',
                                    value: null,
                                });
                                deleteError({ key: 'numberOfTrailers' });
                            }
                        }}
                        options={[
                            { value: 'pcv', displayValue: 'PCV' },
                            { value: 'gcv', displayValue: 'GCV' },
                            { value: 'tractor', displayValue: 'Tractor' },
                        ]}
                    />

                    <InputField
                        name={'make'}
                        style={{ width: '100px' }}
                        handleClick={() => {
                            setMmvType('make');
                            setShowMmvModal(true);
                        }}
                        placeholder={'Select make and model'}
                        value={state['make']?.name}
                        // handleBlur={() => setTouched(true)}
                        readOnly={true}
                        hideLabelText={showError && errors?.make}
                        inputContainerStyles={{
                            marginTop: '1.5rem',
                            height: '50px',
                            marginBottom: showError && errors?.make && '1.5rem',
                        }}
                        showError={showError && errors?.make}
                        errors={{
                            make: errors?.make,
                        }}
                        touched={{}}
                        dropdown
                        pb={true}
                    />

                    <InputField
                        name={'model'}
                        style={{ width: '100px' }}
                        handleClick={() => {
                            setMmvType('model');
                            setShowMmvModal(true);
                        }}
                        placeholder={'Select fuel type'}
                        value={state['model']?.name}
                        readOnly={true}
                        hideLabelText={showError && errors?.model}
                        inputContainerStyles={{
                            marginTop: '1.5rem',
                            height: '50px',
                            marginBottom:
                                showError && errors?.model && '1.5rem',
                        }}
                        showError={showError && errors?.model}
                        errors={{
                            model: errors?.model,
                        }}
                        touched={{}}
                        dropdown
                        pb={true}
                    />

                    <InputField
                        name={'variant'}
                        style={{ width: '100px' }}
                        handleClick={() => {
                            setMmvType('variant');
                            setShowMmvModal(true);
                        }}
                        placeholder={'Select variant'}
                        value={state['variant']?.name}
                        // handleBlur={() => setTouched(true)}
                        readOnly={true}
                        hideLabelText={showError && errors?.variant}
                        inputContainerStyles={{
                            marginTop: '1.5rem',
                            height: '50px',
                            marginBottom:
                                showError && errors?.variant && '1.5rem',
                        }}
                        showError={showError && errors?.variant}
                        errors={{
                            variant: errors?.variant,
                        }}
                        touched={{}}
                        dropdown
                        pb={true}
                    />

                    {vehicleType?.value === 'tractor' && (
                        <InputField
                            errors={{
                                numberOfTrailers: errors?.numberOfTrailers,
                            }}
                            placeholder="Number of trailers"
                            name="numberOfTrailers"
                            value={numberOfTrailers}
                            handleClick={() => {
                                setShowTrailersModal(true);
                            }}
                            touched={{}}
                            showError={showError && errors?.numberOfTrailers}
                            readOnly
                            dropdown
                            className="fs-exclude"
                        />
                    )}

                    <div id={'trailersInfo'}>
                        {vehicleType?.value === 'tractor' &&
                            numberOfTrailers &&
                            numberOfTrailers > 1 && (
                                <>
                                    {trailersInfo?.map((trailer, i) => {
                                        const {
                                            registrationNumber,
                                            chassisNumber,
                                            label,
                                            id,
                                            type,
                                        } = trailer;
                                        return (
                                            <TrailerInputGroup
                                                key={id}
                                                id={id}
                                                registrationNumber={
                                                    registrationNumber
                                                }
                                                chassisNumber={chassisNumber}
                                                type={type}
                                                setTrailerInfo={setTrailerInfo}
                                                // Passing error messages seperatley to avaoid re-render of child component
                                                // Re-render will happen if object is passed
                                                registrationNumberError={
                                                    trailerInfoErrors![i]
                                                        ?.registrationNumber
                                                }
                                                chassisNumberError={
                                                    trailerInfoErrors![i]
                                                        ?.chassisNumber
                                                }
                                                typeError={
                                                    trailerInfoErrors![i]?.type
                                                }
                                                showError={showError}
                                                label={label}
                                                setError={setTrailerInfoError}
                                                deleteError={
                                                    deleteTrailerInfoError
                                                }
                                                // setShowError={setShowError}
                                            />
                                        );
                                    })}
                                </>
                            )}
                    </div>

                    <FormikInput
                        name="registrationNumber"
                        initialValue={registrationNumber}
                        validate={(values) => {
                            const registrationNumberErrors =
                                validate.registrationNumber(values);
                            if (registrationNumberErrors.registrationNumber) {
                                setError({
                                    key: 'registrationNumber',
                                    value: registrationNumberErrors.registrationNumber,
                                });
                            } else {
                                deleteError({
                                    key: 'registrationNumber',
                                });
                            }
                            return registrationNumberErrors;
                        }}
                        value={registrationNumber}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Registration number"
                                name="registrationNumber"
                                onChange={(value, name) => {
                                    setFormData({
                                        key: name,
                                        value: value?.toUpperCase(),
                                    });
                                }}
                                value={registrationNumber}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <FormikInput
                        name="registrationYearMonth"
                        initialValue={
                            registrationYear || registrationMonth
                                ? ` ${registrationYear} ${registrationMonth}`
                                : ''
                        }
                        validate={() => {
                            return {};
                        }}
                        value={
                            registrationYear || registrationMonth
                                ? `${registrationYear} ${registrationMonth}`
                                : ''
                        }
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        readOnly
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{
                                    ...errors,
                                    ...formikErrors,
                                }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Registration year and month"
                                name="registrationYearMonth"
                                readOnly
                                handleClick={() => {
                                    setShowRegistrationYearMonthCalendar(true);
                                }}
                                value={
                                    registrationYear || registrationMonth
                                        ? `${registrationYear} ${registrationMonth}`
                                        : ''
                                }
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <FormikInput
                        name="engineNumber"
                        initialValue={engineNumber}
                        validate={(values) => {
                            const engineNumberErrors =
                                validate.engineNumber(values);
                            if (engineNumberErrors.engineNumber) {
                                setError({
                                    key: 'engineNumber',
                                    value: engineNumberErrors.engineNumber,
                                });
                            } else {
                                deleteError({
                                    key: 'engineNumber',
                                });
                            }
                            return engineNumberErrors;
                        }}
                        value={engineNumber}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Engine/motor number"
                                name="engineNumber"
                                onChange={(value, name) => {
                                    setFormData({
                                        key: name,
                                        value: value?.toUpperCase(),
                                    });
                                }}
                                value={engineNumber}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <FormikInput
                        name="chassisNumber"
                        initialValue={chassisNumber}
                        validate={(values) => {
                            const chassisNumberErrors =
                                validate.chassisNumber(values);
                            if (chassisNumberErrors.chassisNumber) {
                                setError({
                                    key: 'chassisNumber',
                                    value: chassisNumberErrors.chassisNumber,
                                });
                            } else {
                                deleteError({
                                    key: 'chassisNumber',
                                });
                            }
                            return chassisNumberErrors;
                        }}
                        value={chassisNumber}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Chassis number"
                                name="chassisNumber"
                                onChange={(value, name) => {
                                    setFormData({
                                        key: name,
                                        value: value?.toUpperCase(),
                                    });
                                }}
                                value={chassisNumber}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <RadioField
                        field="Is vehicle financed?"
                        selectedValue={isVehicleFinanced}
                        name={'isVehicleFinanced'}
                        setValue={(selection, key) => {
                            setFormData({ key: 'financierName', value: '' });
                            if (selection?.displayValue === 'Yes') {
                                setError({
                                    key: 'financierName',
                                    value: 'Financier name is required',
                                });
                            } else {
                                deleteError({
                                    key: 'financierName',
                                });
                            }
                            setFormData({ key, value: selection });
                        }}
                        options={[
                            { value: '0', displayValue: 'No' },
                            { value: '1', displayValue: 'Yes' },
                        ]}
                    />

                    {isVehicleFinanced?.displayValue === 'Yes' && (
                        <FormikInput
                            name="financierName"
                            initialValue={financierName}
                            validate={(values) => {
                                const financierNameErrors =
                                    validate.financierName(values);
                                if (financierNameErrors.financierName) {
                                    setError({
                                        key: 'financierName',
                                        value: financierNameErrors.financierName,
                                    });
                                } else {
                                    deleteError({
                                        key: 'financierName',
                                    });
                                }
                                return financierNameErrors;
                            }}
                            value={financierName}
                            handleNext={() => null}
                            buttonText=""
                            validateOnBlur
                            customInput={({
                                errors: formikErrors,
                                ...props
                            }: any) => (
                                <InputField
                                    errors={{ ...errors, ...formikErrors }}
                                    {...props}
                                    showError={showError}
                                    setShowError={setShowError}
                                    placeholder="Financier name"
                                    name="financierName"
                                    onChange={(value, name) => {
                                        setFormData({ key: name, value });
                                    }}
                                    value={financierName}
                                    className="fs-exclude"
                                />
                            )}
                            enableReinitialize
                        />
                    )}

                    <RadioField
                        field="Is previous policy expired?"
                        selectedValue={isPreviousPolicyExpired}
                        name={'isPreviousPolicyExpired'}
                        setValue={(selection, key) => {
                            setFormData({ key, value: selection });
                        }}
                        options={[
                            { value: '1', displayValue: 'Yes' },
                            {
                                value: '0',
                                displayValue: 'No',
                                isDisabled: true,
                            },
                        ]}
                    />

                    <div style={{ marginTop: '1.5rem' }}>
                        <Calendar
                            title="Previous policy expiry date"
                            defaultDate={previousPolicyExpiryDate}
                            onSelect={(date) => {
                                setFormData({
                                    key: 'previousPolicyExpiryDate',
                                    value: date,
                                });
                            }}
                            enableWindow={{
                                start:
                                    isPreviousPolicyExpired?.displayValue ===
                                    'Yes'
                                        ? new Date(
                                              new Date().setFullYear(
                                                  new Date().getFullYear() - 10
                                              )
                                          )
                                        : new Date(
                                              new Date().setDate(
                                                  new Date().getDate() - 1
                                              )
                                          ),
                                current: new Date(
                                    new Date().setDate(new Date().getDate() - 1)
                                ),
                                end:
                                    isPreviousPolicyExpired?.displayValue ===
                                    'Yes'
                                        ? new Date(
                                              new Date().setDate(
                                                  new Date().getDate() - 1
                                              )
                                          )
                                        : new Date(
                                              new Date().setFullYear(
                                                  new Date().getFullYear() + 1
                                              )
                                          ),
                            }}
                            isDesktop={!isMobile}
                        />
                    </div>

                    <RadioField
                        field="Claimed in previous policy?"
                        selectedValue={claimedInPreviousPolicy}
                        name={'claimedInPreviousPolicy'}
                        setValue={(selection, key) => {
                            setFormData({
                                key,
                                value: selection,
                            });
                        }}
                        options={[
                            { value: '0', displayValue: 'No' },
                            { value: '1', displayValue: 'Yes' },
                        ]}
                    />

                    <FormikInput
                        name="previousPolicyInsurer"
                        initialValue={previousPolicyInsurer}
                        validate={(values) => {
                            const previousPolicyInsurerErrors =
                                validate.previousPolicyInsurer(values);
                            if (
                                previousPolicyInsurerErrors.previousPolicyInsurer
                            ) {
                                setError({
                                    key: 'previousPolicyInsurer',
                                    value: previousPolicyInsurerErrors.previousPolicyInsurer,
                                });
                            } else {
                                deleteError({
                                    key: 'previousPolicyInsurer',
                                });
                            }
                            return previousPolicyInsurerErrors;
                        }}
                        value={previousPolicyInsurer}
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                showError={showError}
                                setShowError={setShowError}
                                placeholder="Previous policy insurer"
                                name="previousPolicyInsurer"
                                onChange={(value, name) => {
                                    setFormData({
                                        key: name,
                                        value: value?.toUpperCase(),
                                    });
                                }}
                                value={previousPolicyInsurer}
                                className="fs-exclude"
                            />
                        )}
                        enableReinitialize
                    />

                    <InputField
                        name={'addonCovers'}
                        style={{ width: '100px' }}
                        handleClick={() => setShowAddonsModal(true)}
                        placeholder={'Add add-on covers'}
                        value={
                            selectedAddons?.length
                                ? `${selectedAddons.length} selected`
                                : ''
                        }
                        readOnly={true}
                        inputContainerStyles={{
                            marginTop: '1.5rem',
                            height: '50px',
                        }}
                        errors={{}}
                        touched={{}}
                        dropdown
                        pb={true}
                    />

                    <SelectedAddonsSummary
                        selectedAddons={selectedAddons ?? []}
                    />

                    <InputField
                        errors={{}}
                        placeholder="Policy start date"
                        name="policyStartDate"
                        value={formatDate(policyStartDate)}
                        readOnly
                        calendar
                        className="fs-exclude"
                    />

                    {showPremiumBreakdown && (
                        <PremiumBreakdownBox>
                            {hasApiPricing &&
                                (quotePlan?.covers?.length ?? 0) > 0 &&
                                (quotePlan?.covers ?? []).map((c, idx) => (
                                    <PremiumBreakdownRow
                                        key={c.name ?? `cover_${idx}`}
                                    >
                                        <span>
                                            {c.name ??
                                                'Base third-party premium'}
                                        </span>
                                        <span>
                                            {formatAddonRupee(
                                                c.price.netPremium ??
                                                    c.price.grossPremium ??
                                                    0
                                            )}
                                        </span>
                                    </PremiumBreakdownRow>
                                ))}
                            {!hasApiPricing && totalAmount != null && (
                                <PremiumBreakdownRow>
                                    <span>Base third-party premium</span>
                                    <span>{formatAddonRupee(totalAmount)}</span>
                                </PremiumBreakdownRow>
                            )}
                            {quotePlan?.addons?.length
                                ? quotePlan.addons.map((line) => (
                                      <PremiumBreakdownRow
                                          key={line.addonId ?? line.name ?? ''}
                                      >
                                          <span>
                                              {formatAddonDisplayName(
                                                  line.name ?? ''
                                              )}{' '}
                                          </span>
                                          <span>
                                              {formatAddonRupee(
                                                  line.price ?? 0
                                              )}
                                          </span>
                                      </PremiumBreakdownRow>
                                  ))
                                : (selectedAddons ?? []).map((addon) => (
                                      <PremiumBreakdownRow key={addon.id}>
                                          <span>
                                              {formatAddonDisplayName(
                                                  addon.name
                                              )}{' '}
                                          </span>
                                          <span>
                                              {formatAddonRupee(
                                                  addon.price ?? 0
                                              )}
                                          </span>
                                      </PremiumBreakdownRow>
                                  ))}
                            <PremiumBreakdownRow>
                                <span>Subtotal (excl. GST)</span>
                                <span>{formatAddonRupee(subtotalExclGst)}</span>
                            </PremiumBreakdownRow>
                            <PremiumBreakdownRow>
                                <span>Total GST</span>
                                <span>{formatAddonRupee(gstAmount)}</span>
                            </PremiumBreakdownRow>
                            <PremiumBreakdownTotal>
                                <span>Total payable (incl. GST)</span>
                                <span>
                                    {formatAddonRupee(grandTotalInclGst)}
                                </span>
                            </PremiumBreakdownTotal>
                        </PremiumBreakdownBox>
                    )}
                    <FormikInput
                        name="totalAmount"
                        initialValue={
                            grandTotalInclGst
                                ? grandTotalInclGst.toString()
                                : ''
                        }
                        validate={() => ({})}
                        value={
                            grandTotalInclGst
                                ? grandTotalInclGst.toString()
                                : ''
                        }
                        handleNext={() => null}
                        buttonText=""
                        validateOnBlur
                        type="number"
                        readOnly
                        customInput={({
                            errors: formikErrors,
                            ...props
                        }: any) => (
                            <InputField
                                errors={{ ...errors, ...formikErrors }}
                                {...props}
                                // showError={showError}
                                // setShowError={setShowError}
                                placeholder="Total premium (incl. GST)"
                                name="totalAmount"
                                // onChange={(value, name) => {
                                //     setFormData({
                                //         key: name,
                                //         value,
                                //     });
                                // }}
                                value={
                                    grandTotalInclGst
                                        ? grandTotalInclGst.toString()
                                        : ''
                                }
                                className="fs-exclude"
                                type="number"
                                readOnly
                            />
                        )}
                        enableReinitialize
                    />

                    <Modal
                        header={
                            <ModalHeading>
                                {getHeadingText(mmvType)}
                            </ModalHeading>
                        }
                        maxWidth="600px"
                        show={isMmvApiSuccess && showMmvModal && !!mmvType}
                        onHide={() => {
                            setShowMmvModal(false);
                        }}
                        padding={isMobile ? '24px 20px 20px' : '32px'}
                        showOverlayBackground={true}
                        wrapperStyles={{
                            padding: '32px 20px 20px',
                            maxHeight: '95vh',
                            borderRadius: '16px 16px 0px 0px',
                        }}
                        styles={{
                            minWidth: '500px',
                        }}
                    >
                        <MmvOptions
                            handleClick={(options) => {
                                const { value, type } = options;
                                setFormData({ key: type, value });
                                deleteError({ key: type });
                                handleNextModal({ type: mmvType, setMmvType });
                            }}
                            queryType={mmvType}
                            fuelType={fuelType}
                            popularVehicles={popularVehicles}
                            vehicles={vehicles}
                            variantType={variantTypes}
                            vehicleType={vehicleType?.value}
                        />
                    </Modal>
                    <Modal
                        header={
                            <ModalHeading>Select add-on covers</ModalHeading>
                        }
                        maxWidth="600px"
                        show={showAddonsModal}
                        onHide={() => {
                            setShowAddonsModal(false);
                        }}
                        padding={isMobile ? '24px 20px 20px' : '32px'}
                        showOverlayBackground={true}
                        wrapperStyles={{
                            padding: '32px 20px 20px',
                            maxHeight: '95vh',
                            borderRadius: '16px 16px 0px 0px',
                        }}
                        styles={{
                            minWidth: '500px',
                        }}
                    >
                        <AddonsOptions
                            addons={addonsCatalog}
                            selectedIds={selectedAddonIds}
                            onAdd={(addon) => {
                                if (selectedAddonIds.has(addon.id)) return;
                                setFormData({
                                    key: 'selectedAddons',
                                    value: [...(selectedAddons ?? []), addon],
                                });
                            }}
                            onRemove={(id) => {
                                setFormData({
                                    key: 'selectedAddons',
                                    value: (selectedAddons ?? []).filter(
                                        (a) => a.id !== id
                                    ),
                                });
                            }}
                        />
                    </Modal>

                    <Modal
                        header={
                            <ModalHeading>
                                Select registration year and month
                            </ModalHeading>
                        }
                        maxWidth="600px"
                        show={showRegistrationyarMonthCalendar}
                        onHide={() => {
                            setShowRegistrationYearMonthCalendar(false);
                        }}
                        padding={isMobile ? '24px 20px 20px' : '32px'}
                        showOverlayBackground={true}
                        wrapperStyles={{
                            padding: '32px 20px 20px',
                            maxHeight: '95vh',
                            borderRadius: '16px 16px 0px 0px',
                        }}
                        styles={{
                            minWidth: '500px',
                        }}
                    >
                        <>
                            <YearMonthSelector
                                showModal={showRegistrationyarMonthCalendar}
                                setShowModal={
                                    setShowRegistrationYearMonthCalendar
                                }
                                monthOptions={months}
                                yearOptions={years}
                                activeMonth={registrationMonth}
                                activeYear={registrationYear}
                                setActiveMonth={(value) => {
                                    setFormData({
                                        key: 'registrationMonth',
                                        value,
                                    });
                                }}
                                setActiveYear={(value) => {
                                    setFormData({
                                        key: 'registrationYear',
                                        value,
                                    });
                                }}
                            />
                        </>
                    </Modal>

                    <Modal
                        header={
                            <ModalHeading>
                                Select number of trailers
                            </ModalHeading>
                        }
                        maxWidth="600px"
                        show={showTrailersModal}
                        onHide={() => {
                            setShowTrailersModal(false);
                        }}
                        padding={isMobile ? '24px 20px 20px' : '32px'}
                        showOverlayBackground={true}
                        wrapperStyles={{
                            padding: '32px 20px 20px',
                            maxHeight: '95vh',
                            borderRadius: '16px 16px 0px 0px',
                        }}
                        styles={{
                            minWidth: '500px',
                        }}
                    >
                        {[...Array(5).keys()]
                            .map((k) => k + 1)
                            .map((trailer) => {
                                return (
                                    <Trailer
                                        key={trailer}
                                        onClick={() => {
                                            setFormData({
                                                key: 'numberOfTrailers',
                                                value: trailer,
                                            });
                                            deleteError({
                                                key: 'numberOfTrailers',
                                            });
                                            setShowTrailersModal(false);
                                        }}
                                    >
                                        {trailer}
                                    </Trailer>
                                );
                            })}
                    </Modal>

                    <Modal
                        header={
                            <ModalHeading>
                                Form submitted successfully
                            </ModalHeading>
                        }
                        maxWidth="600px"
                        show={showFormSuccessModal}
                        onHide={() => {}}
                        padding={isMobile ? '24px 20px 20px' : '32px'}
                        showOverlayBackground={true}
                        wrapperStyles={{
                            padding: '32px 20px 20px',
                            maxHeight: '95vh',
                            borderRadius: '16px 16px 0px 0px',
                        }}
                        styles={{
                            minWidth: '500px',
                        }}
                        disableOverlayClose
                        hideCloseButton
                    >
                        <div style={{ width: '100%', marginTop: '1rem' }}>
                            <PrimaryButton
                                onClick={() => {
                                    window.location.reload();
                                }}
                                isFullWidth
                            >
                                Punch new policy
                            </PrimaryButton>
                        </div>
                    </Modal>
                </Container>
                <ButtonContainer>
                    <PrimaryButton
                        onClick={() => {
                            if (isEmpty(errors)) {
                                submitFormData();
                            } else {
                                dispatch({
                                    type: ACTIONS.SET_SHOW_ERROR,
                                    payload: true,
                                });
                                setShouldScrollIntoView(true);
                            }
                        }}
                    >
                        Submit
                    </PrimaryButton>
                </ButtonContainer>
            </Wrapper>
        </>
    );
};

export default Form;
